import styles from './filter-bar.module.css'

import cn from 'clsx'
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { selectIsUnifiedSearchEnabled } from '@disco/state-management/reducers/search-page'
import { hasFilterValue } from '@disco/utils/search-page'

import type { FilterState } from 'types/filter-page'

import { Flex } from 'components/ds'
import {
  FilterActionAddButton,
  FilterActionClearAllButton,
  SelectedFilterBadges,
} from 'components/search/search-filters'
import OverflowFilterBadges from 'components/search/search-filters/overflow-badges'

import { useSelector } from 'hooks/use-redux'

import { useFiltersDefinition } from '../utils'

const isFilterValueActive = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

type Props = {
  filters: FilterState[]
  /** A search term is itself clearable state. */
  hasQuery: boolean
  onChange: (_filters: FilterState[]) => void
  onClearAll: (_filters: FilterState[]) => void
  // Adds empty fixed-filter badges on mount; off in Browse, where that would trigger a search
  seedFixedFilters?: boolean
  // Moves badges that don't fit into a "+N more" popover instead of wrapping (Browse only); ignores seedFixedFilters
  collapseOverflow?: boolean
  /** Rendered right after "+ Filter" (the search page's Source filter). */
  afterAddButton?: React.ReactNode
}

// Add-filter button, filter badges and reset button, shared by the search page and Browse
const FilterBar = ({
  filters,
  hasQuery,
  onChange,
  onClearAll,
  seedFixedFilters = true,
  collapseOverflow = false,
  afterAddButton,
}: Props): React.ReactNode => {
  const definitions = useFiltersDefinition()
  const unifiedSearchEnabled = useSelector(selectIsUnifiedSearchEnabled)

  const { hasActiveFilterValues, hasLegacyFilterValues, hasOperator } = useMemo(
    () =>
      filters.reduce(
        (acc, f) => ({
          hasActiveFilterValues:
            acc.hasActiveFilterValues || isFilterValueActive(f.value),
          // Looser than isFilterValueActive on purpose: legacy Reset also
          // treats an empty array as clearable.
          hasLegacyFilterValues:
            acc.hasLegacyFilterValues || hasFilterValue(f.value),
          hasOperator: acc.hasOperator || !!f.operator,
        }),
        {
          hasActiveFilterValues: false,
          hasLegacyFilterValues: false,
          hasOperator: false,
        },
      ),
    [filters],
  )

  const seedDefaultState = seedFixedFilters && !unifiedSearchEnabled
  const hasClearableState = hasActiveFilterValues || hasOperator || hasQuery
  const showClearAll = unifiedSearchEnabled ? hasClearableState : true
  const isClearAllDisabled =
    !unifiedSearchEnabled && !hasLegacyFilterValues && !hasQuery

  // A fade shows only when content is hidden on that side
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false })

  const updateScrollEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const left = el.scrollLeft > 0
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setScrollEdges(prev =>
      prev.left === left && prev.right === right ? prev : { left, right },
    )
  }, [])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || !collapseOverflow) return

    // Resizing fires no scroll event, so the fades update here; Reset is left to the effect below
    const observer = new ResizeObserver(updateScrollEdges)
    observer.observe(el)
    // Badges collapsing into "+N more" resize the content, not the scroll box
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [collapseOverflow, updateScrollEdges])

  // Separate effect so filter edits don't reconnect the observer
  useLayoutEffect(() => {
    if (collapseOverflow) updateScrollEdges()
  }, [collapseOverflow, updateScrollEdges, filters, showClearAll])

  // Same reset button in both layouts
  const clearAllButton = showClearAll ? (
    <FilterActionClearAllButton
      definitions={definitions}
      disabled={isClearAllDisabled}
      seedDefaultState={seedDefaultState}
      onChange={onClearAll}
    />
  ) : null

  return (
    <>
      <FilterActionAddButton
        value={filters}
        definitions={definitions}
        onChange={onChange}
      />
      {afterAddButton}
      {collapseOverflow ? (
        // Scrolls only if the row still doesn't fit after badges collapse into "+N more"
        <div
          className={cn(styles.scrollFade, {
            [styles.showLeftFade]: scrollEdges.left,
            [styles.showRightFade]: scrollEdges.right,
          })}
        >
          <Flex
            ref={scrollRef}
            onScroll={updateScrollEdges}
            direction="row"
            wrap="nowrap"
            minWidth="0"
            align="center"
            gap={{ initial: '1', sm: '2' }}
            overflowX="auto"
            mr="3"
            className={styles.scrollTrack}
          >
            <OverflowFilterBadges
              value={filters}
              onChange={onChange}
              trailing={clearAllButton}
            />
          </Flex>
        </div>
      ) : (
        <>
          <SelectedFilterBadges
            value={filters}
            definitions={definitions}
            seedDefaultState={seedDefaultState}
            onChange={onChange}
          />
          {clearAllButton}
        </>
      )}
    </>
  )
}

export default FilterBar
